import { useCallback, useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';

import SelectMulti from 'app/components/Select/SelectMulti';
import Typography from 'app/components/Typography';

import { DEFAULT_ITEM_FILTER, ITEM_STATUS_OPTIONS } from 'app/modules/inventory/bins/core/constant';
import { ItemFilterParams } from 'app/modules/inventory/items/core/type';
import { Body, Header, Main } from 'app/modules/inventory/stock-run/components/StockRunFilter';
import { useChangeParams } from 'app/modules/query-state/hooks';
import { useQueryParamStore } from 'app/modules/query-state/store';

import isEqual from 'lodash/isEqual';

interface ItemFilterProps {
  openFilter: boolean;
  setIsOpenFilter: (open: boolean) => void;
}

const ItemFilter = ({ openFilter, setIsOpenFilter }: ItemFilterProps) => {
  const onChangeParams = useChangeParams().onChangeParams;
  const curParams = useQueryParamStore((s) => s.state.curParams);

  const [filter, setFilter] = useState<ItemFilterParams>(DEFAULT_ITEM_FILTER);
  const [menuIsOpen, setMenuIsOpen] = useState(false);

  useEffect(() => {
    if (!openFilter || isEqual(curParams, filter)) return;
    setFilter({
      statuses: Array.isArray(curParams.statuses)
        ? curParams.statuses.map((status) => Number(status))
        : [],
    });
  }, [curParams, openFilter]);

  const handleSelectChange = useCallback(
    (key: keyof ItemFilterParams) => (newValue: { value: string | number }[]) => {
      setFilter((prev) => ({
        ...prev,
        [key]: newValue.map((el) => Number(el.value)),
      }));
      setMenuIsOpen(true);
    },
    [],
  );

  const handleCloseFilter = () => {
    setIsOpenFilter(false);
  };

  const handleApplyFilter = () => {
    onChangeParams({
      ...filter,
      page: 1,
    });
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setFilter(DEFAULT_ITEM_FILTER);
    onChangeParams(DEFAULT_ITEM_FILTER);
    handleCloseFilter();
  };

  const handleMenuClose = () => setMenuIsOpen(false);
  const handleMenuOpen = () => setMenuIsOpen(true);

  const handleSelectedItemStatus = ITEM_STATUS_OPTIONS.filter((el) =>
    filter?.statuses.includes(el.value),
  );

  return (
    <Main>
      <Header>
        <Typography variant="heading-6">Filter</Typography>
        <Typography
          className="clear_all cursor-pointer"
          variant="body-5"
          onClick={handleClearAll}
          color="primary"
        >
          Clear all
        </Typography>
      </Header>
      <Body>
        <Typography variant="body-7" color="gray-700" className="mb-2">
          Status
        </Typography>
        <SelectMulti
          options={ITEM_STATUS_OPTIONS}
          selectedOption={handleSelectedItemStatus}
          handleChange={handleSelectChange('statuses')}
          placeholder="Select status to filter"
          menuIsOpen={menuIsOpen}
          onMenuClose={handleMenuClose}
          openMenuOnClick={handleMenuOpen}
        />

        <div className="d-flex justify-content-end mt-4">
          <Button variant="primary" onClick={handleApplyFilter}>
            Apply
          </Button>
        </div>
      </Body>
    </Main>
  );
};

export default ItemFilter;
